import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import {
    IQuotationsQuotation,
    IQuotationSection,
    IQuotationBlock,
    IQuotationCost,
    IDetailPriceTraveller,
    IQuotationTraveller,
    IQuotationPriceTotal
} from 'libraries/service/resources/quotation-quotations/IQuotationsQuotation';
import {
    QuotationQuotationsResourceService,
    IQuotationsQuotationCreate,
    IQuotationQuotationsSectionsCreate,
    IQuotationQuotationsBlocksCreate,
    IQuotationQuotationsBlockPatch, IQuotationQuotationCategoriesMarginPut
} from 'libraries/service/resources/quotation-quotations/quotation-quotations-resource.service';
import { tap, shareReplay, catchError } from 'rxjs/operators';
import { CalculationMode } from "../../../libraries/interfaces/IWenji";

@Injectable({
    providedIn: "root"
})
export class CachedQuotationService {
    private readonly cachedQuotations: Map<number, Observable<IQuotationsQuotation>> = new Map();

    constructor(private readonly quotationResource: QuotationQuotationsResourceService) {
    }

    public create(request: IQuotationsQuotationCreate): Observable<IQuotationsQuotation> {
        return this.quotationResource.create(request)
            .pipe(
                tap((quotation: IQuotationsQuotation) => {
                    this.cachedQuotations.set(+(quotation.id), of(quotation));
                })
            );
    }

    public get(quotationId: number): Observable<IQuotationsQuotation> {
        if (this.cachedQuotations.has(+quotationId)) {
            return this.cachedQuotations.get(+quotationId);
        }

        return this.cacheAndReturn(+quotationId, this.quotationResource.get(+quotationId));
    }

    public patch(request: Partial<IQuotationsQuotationCreate>, quotationId: number): Observable<IQuotationsQuotation> {
        return this.cacheAndReturn(quotationId, this.quotationResource.patch(request, quotationId));
    }

    public patchTotalPrice(request: IQuotationPriceTotal, quotationId: number): Observable<IQuotationsQuotation> {
        return this.cacheAndReturn(quotationId, this.quotationResource.patchTotalPrice(request, quotationId));
    }

    public patchTraveller(request: IQuotationTraveller, quotationId: number): Observable<IQuotationsQuotation> {
        return this.cacheAndReturn(quotationId, this.quotationResource.patchTraveller(request, quotationId));
    }

    public createSection(request: IQuotationQuotationsSectionsCreate, quotationId: number): Observable<IQuotationSection> {
        return this.quotationResource.createSection(request, quotationId)
            .pipe(tap(() => {
                this.removeFromCache(quotationId);
            }));
    }

    public createBlock(quotationId: number, request: IQuotationQuotationsBlocksCreate, sectionId: string): Observable<IQuotationBlock> {
        return this.quotationResource.createBlock(request, sectionId)
            .pipe(tap(() => {
                this.removeFromCache(quotationId);
            }));
    }

    public patchBlock(quotationId: number, request: IQuotationQuotationsBlockPatch, blockId: number): Observable<IQuotationsQuotation> {
        return this.cacheAndReturn(quotationId, this.quotationResource.patchBlock(request, blockId));
    }

    public deleteBlock(quotationId: number, blockId: number): Observable<void> {
        return this.quotationResource.deleteBlock(blockId)
            .pipe(tap(() => {
                this.removeFromCache(quotationId);
            }));
    }

    public patchDetailPriceTravellerCostReturn(quotationId: number, id: number, cost: IQuotationCost): Observable<IQuotationsQuotation> {
        return this.cacheAndReturn(quotationId, this.quotationResource.patchDetailPriceTravellerCostReturn(id, cost));
    }

    public patchTotalPriceCurrency(quotationId: number, id: number, cost: IQuotationCost): Observable<IQuotationsQuotation> {
        return this.cacheAndReturn(quotationId, this.quotationResource.patchTotalPriceCurrency(id, cost));
    }

    public patchQuotationBlock(quotationId: number, id: number, body: unknown): Observable<IQuotationsQuotation> {
        return this.cacheAndReturn(
            quotationId,
            this.quotationResource.patchQuotationBlock(id, body)
        );
    }

    public patchBlockDetailPrice(quotationId: number, id: number, cost: IQuotationCost): Observable<IQuotationsQuotation> {
        return this.cacheAndReturn(quotationId, this.quotationResource.patchBlockDetailPrice(id, cost));
    }

    public patchDetailPriceTravellerCostSimple(quotationId: number, id: number, cost: IQuotationCost): Observable<IDetailPriceTraveller> {
        return this.quotationResource.patchDetailPriceTravellerCostSimple(id, cost)
            .pipe(tap(() => {
                this.removeFromCache(quotationId);
            }));
    }

    public putQuotationQuotationsCategories(
        quotationId: number,
        calculationMode: CalculationMode,
        request: IQuotationQuotationCategoriesMarginPut
    ): Observable<IQuotationsQuotation> {
        return this.cacheAndReturn(quotationId, this.quotationResource.putQuotationQuotationsCategories(quotationId, calculationMode, request));
    }

    private cacheAndReturn(id: number, quotation: Observable<IQuotationsQuotation>): Observable<IQuotationsQuotation> {
        const response: Observable<IQuotationsQuotation> =
            quotation.pipe(
                shareReplay(1),
                catchError((error: Error) => {
                    this.cachedQuotations.delete(+id);
                    return throwError(error);
                }));

        this.cachedQuotations.set(+id, response);
        return response;
    }

    private removeFromCache(id: number): void {
        this.cachedQuotations.delete(+id);
    }
}
